import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useAuth } from '@/context/auth-provider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Crown, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface RouteGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

// Platform Admin Route Guard (for super admins and support staff)
export const PlatformAdminRoute = ({ children, fallback }: RouteGuardProps) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isLoading } = useUserRoles();

  if (authLoading || isLoading) {
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!roles.isPlatformAdmin) {
    return fallback || (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Access Denied</CardTitle>
            <CardDescription>
              You need platform administrator privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

// Organization Admin Route Guard (for organization admins)
export const OrgAdminRoute = ({ children, fallback }: RouteGuardProps) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isLoading } = useUserRoles();

  console.log('[ORG-ADMIN-ROUTE] Route guard check:', {
    authLoading,
    rolesLoading: isLoading,
    user: !!user,
    userId: user?.id,
    roles: {
      platformRole: roles.platformRole,
      organizationRole: roles.organizationRole,
      isPlatformAdmin: roles.isPlatformAdmin,
      isOrgAdmin: roles.isOrgAdmin,
      hasAccessToAllOrgs: roles.hasAccessToAllOrgs
    }
  });

  if (authLoading) {
    console.log('[ORG-ADMIN-ROUTE] Auth still loading, showing skeleton');
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    console.log('[ORG-ADMIN-ROUTE] Roles still loading, showing skeleton');
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    console.log('[ORG-ADMIN-ROUTE] No user, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  const hasAccess = roles.isOrgAdmin || roles.isPlatformAdmin;
  console.log('[ORG-ADMIN-ROUTE] Access check result:', { hasAccess });

  if (!hasAccess) {
    console.log('[ORG-ADMIN-ROUTE] Access denied, insufficient permissions');
    return fallback || (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Access Denied</CardTitle>
            <CardDescription>
              You need organization administrator privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  console.log('[ORG-ADMIN-ROUTE] Access granted, rendering children');
  return <>{children}</>;
};

// General Admin Route Guard (any kind of admin)
export const AdminRoute = ({ children, fallback }: RouteGuardProps) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isLoading } = useUserRoles();

  if (authLoading || isLoading) {
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!roles.isAdmin) {
    return fallback || (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Crown className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Access Denied</CardTitle>
            <CardDescription>
              You need administrator privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

// Authenticated Route Guard (any authenticated user)
export const AuthenticatedRoute = ({ children, fallback }: RouteGuardProps) => {
  const { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};